import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ContentContext } from "../contexts/ContentContext";
import SectorIcon from "./SectorIcon";
import { FiArrowRight } from "react-icons/fi";

// Self-hosted imagery for the active-sector cards. All assets live in
// public/images and reflect actual North Star operations.
const SECTOR_IMAGES = {
    dates: "/images/date_ontree.jpg",
    silk: "/images/silk-banner.jpg",
    procurement: "/images/date_warehouse.jpg",
    logistics: "/images/fumigation-process.jpg",
};

export default function SectorGrid() {
    const { data } = useContext(ContentContext);
    const current = data.sectors.filter((s) => s.status === "current");
    const future = data.sectors.filter((s) => s.status !== "current");

    return (
        <>
            <section className="relative py-20 md:py-28 bg-white overflow-hidden">
                {/* Faint brand-grid texture in the corner */}
                <div className="absolute top-0 right-0 w-[420px] h-[420px] brand-pattern opacity-50 pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl mb-14">
                        <div className="inline-flex items-center gap-3 mb-4">
                            <span className="h-px w-10 bg-gold-500" />
                            <span className="text-xs font-bold text-brand-emerald tracking-[0.22em] uppercase">
                                {data.currentWork.eyebrow}
                            </span>
                        </div>
                        <h2 className="font-serif text-3xl md:text-5xl font-bold text-navy-900 mb-4 leading-tight">
                            {data.currentWork.title}
                        </h2>
                        <p className="text-lg text-navy-700/80 leading-relaxed">
                            {data.currentWork.subtitle}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {current.map((s) => (
                            <Link
                                key={s.id}
                                to={`/sectors#${s.id}`}
                                className="group relative bg-gradient-to-br from-brand-deep via-navy-900 to-brand-emerald text-white rounded-3xl overflow-hidden hover:shadow-glow transition-all duration-300"
                            >
                                {/* Photo backdrop */}
                                {SECTOR_IMAGES[s.id] && (
                                    <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition">
                                        <img
                                            src={SECTOR_IMAGES[s.id]}
                                            alt=""
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                            onError={(e) => (e.currentTarget.style.display = "none")}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-brand-deep via-brand-deep/60 to-transparent" />
                                    </div>
                                )}

                                <div className="relative p-8">
                                    <div className="absolute top-5 end-5 text-[10px] font-bold text-navy-900 bg-gold-400 px-2.5 py-1 rounded-full tracking-widest uppercase">
                                        Active
                                    </div>
                                    <div className="w-14 h-14 rounded-xl bg-gold-400 text-navy-900 flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-3 transition shadow-glow">
                                        <SectorIcon name={s.icon} />
                                    </div>
                                    <h3 className="font-serif text-2xl font-bold mb-3">
                                        {s.title}
                                    </h3>
                                    <p className="text-white/85 text-sm leading-relaxed mb-5 max-w-md">
                                        {s.short}
                                    </p>
                                    <div className="inline-flex items-center gap-2 text-sm font-semibold text-gold-300 group-hover:gap-3 transition-all">
                                        <span>Learn more</span>
                                        <FiArrowRight className="w-4 h-4 rtl:rotate-180" />
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative py-20 bg-navy-50 border-t border-navy-100 overflow-hidden">
                <div className="absolute inset-0 brand-pattern opacity-30 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-2xl mb-12">
                        <div className="inline-flex items-center gap-3 mb-4">
                            <span className="h-px w-10 bg-brand-leaf" />
                            <span className="text-xs font-bold text-navy-700 tracking-[0.22em] uppercase">
                                {data.futureWork.eyebrow}
                            </span>
                        </div>
                        <h2 className="font-serif text-2xl md:text-3xl font-bold text-navy-900 mb-3">
                            {data.futureWork.title}
                        </h2>
                        <p className="text-base text-navy-700/80 leading-relaxed">
                            {data.futureWork.subtitle}
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {future.map((s) => (
                            <Link
                                key={s.id}
                                to={`/sectors#${s.id}`}
                                className="group bg-white border border-navy-100 rounded-2xl p-5 hover:border-gold-300 hover:shadow-card transition-all duration-300"
                            >
                                <div className="w-10 h-10 rounded-lg bg-navy-100 group-hover:bg-brand-emerald text-brand-emerald group-hover:text-gold-300 flex items-center justify-center mb-4 transition">
                                    <SectorIcon name={s.icon} />
                                </div>
                                <h3 className="font-serif text-sm font-bold text-navy-900 mb-1.5 leading-snug">
                                    {s.title}
                                </h3>
                                <p className="text-xs text-navy-700/70 leading-relaxed">
                                    {s.short}
                                </p>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
